import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import ProgressBar from "../../UI/ProgressBar/ProgressBar";
import ScoreCircle from "../../UI/ScoreCircle/ScoreCircle";
import AlertTag from "../../UI/AlertTag/AlertTag";

import { Poll } from "../../../interfaces/Poll";
import { Score } from "../../../interfaces/Score";
import styles from "./PollInfoPanel.module.css";

interface PollInfoPanelProps {
  poll: Poll;
  answers: {
    question_id?: number;
    answered: boolean;
    answer_id?: number;
    required: boolean;
  }[];
  result: number;
  score: Score | undefined;
  alertsMessages?: string[];
}

const PollInfoPanel: React.FC<PollInfoPanelProps> = ({
  poll,
  answers,
  result,
  score,
  alertsMessages = [],
}) => {
  const { t } = useTranslation();

  // ProgressBar calculations
  const requiredAnswers = answers.filter((a) => a.required);
  const answeredRequired = requiredAnswers.filter((a) => a.answered).length;

  // Result content for score display
  const resultContent = (
    <div className={`${styles.resultContent} ${styles.infoAreaTextResult}`}>
      <ScoreCircle
        rules={score?.score_rules || []}
        lastScore={result}
        secondLastScore={undefined}
      />
      <span>{result}</span>
    </div>
  );

  // Alert content for alerts display (memoized to avoid re-renders)
  const alertContent = useMemo(() => {
    if (alertsMessages.length === 0) return null;
    return alertsMessages.map((alertText, index) => (
      <AlertTag key={index} text={alertText} />
    ));
  }, [alertsMessages]);

  return (
    <>
      <div>
        <span className={styles.infoAreaTitle}>{t("description")}:</span>
        <p className={styles.infoAreaText}>{poll.description}</p>
      </div>

      <div>
        <span className={styles.infoAreaTitle}>{t("progress")}:</span>
        <ProgressBar current={answeredRequired} max={requiredAnswers.length} />
      </div>

      <div>
        <span className={styles.infoAreaTitle}>{t("result")}:</span>
        <div className={styles.infoAreaText}>
          {result === 0 ? (
            <span className={styles.placeholder}>
              {t("try_poll_result_placeholder")}
            </span>
          ) : (
            resultContent
          )}
        </div>
      </div>

      <div>
        <span className={styles.infoAreaTitle}>{t("alerts")}:</span>
        <div className={`${styles.infoAreaText} ${styles.alertsContainer}`}>
          {alertContent || (
            <span className={styles.placeholder}>
              {t("try_poll_alerts_placeholder")}
            </span>
          )}
        </div>
      </div>
    </>
  );
};

export default PollInfoPanel;
